/**
 * useQueries.ts - TanStack Query Hooks for API Data Fetching
 * 
 * Architecture:
 * - Uses TanStack Query for caching and data synchronization
 * - Direct Auth0 authentication without hydration gates
 * - Aggressive caching strategy to minimize API calls
 * - Centralized query invalidation for data consistency
 * 
 * Simplified after auth refactoring:
 * - No more waitUntilReady() hydration gates
 * - Direct auth.isAuthenticated checks
 * - Removed skipHydrationGate options
 */
import { computed, ref, type Ref } from 'vue'
import { useQuery, useMutation, useQueryClient, type UseQueryOptions, type UseMutationOptions } from '@tanstack/vue-query'
import { authenticatedGet, authenticatedPost, authenticatedPut, authenticatedDelete, waitForAuthReady, type AuthContext } from '@/utils/api'
import { CACHE_TIMES, invalidateFinancialData, invalidateUserData } from '@/utils/cacheUtils'
import { emitCacheInvalidationEvent, CACHE_EVENTS } from '@/composables/useCacheInvalidation'
import { useEncryption } from '@/composables/useEncryption'
import { useGlobalScopeFilter } from '@/composables/useGlobalScopeFilter'
import { expensesEndpoint, useExpenseHistory } from '@/composables/useExpenseHistory'
import { addTags, expenseTags, removeTags, serializeTags } from '@/utils/tags'
import { isSodiumReady } from '@/utils/encryption'
import {
  computeExpensesByCategory,
  computePendingTotal,
  computeSpendingComparison,
  computeTrackedCategories,
  type CategoryTargetPref,
  type SpendingComparison as ClientSpendingComparison,
  type TrackedCategoriesResponse as ClientTrackedCategoriesResponse,
} from '@/utils/clientAggregation'

// ============================================================================
// TypeScript Interfaces
// ============================================================================
interface Expense {
  ExpenseID?: number
  ExpenseDate: string
  Amount: string | number
  ExpenseCategory?: string | null
  /** Legacy free-text note; read as a tag until the row is next written. */
  AdditionalNotes?: string | null
  /** Pipe-delimited tag list — parse with `expenseTags()` from utils/tags. */
  Tags?: string | null
  MerchantName?: string | null
  PlaidMerchantName?: string | null
  PlaidName?: string | null
  PlaidAccountID?: string | null
  PlaidTransactionID?: string | null
  CategoryConfirmed?: boolean
  IsIncome?: boolean
  /** Plaid transaction location — sealed for encrypted scopes. */
  LocationLat?: number | null
  LocationLon?: number | null
  LocationAddress?: string | null
  LocationCity?: string | null
  LocationRegion?: string | null
  LocationPostalCode?: string | null
  LocationCountry?: string | null
  /**
   * Plaid's "in store" / "online" / "other". Plaintext even for sealed rows.
   * Only present on rows written since the column shipped — the trip inference
   * falls back to PlaidTransactionType for older ones.
   */
  PaymentChannel?: string | null
  PlaidTransactionType?: string | null
  ScopeID?: number
  ScopeName?: string
  Day?: number
  Month?: string
  Year?: number
}

interface ApiResponse<T> {
  success: boolean
  error?: string
  [key: string]: any
}

interface AccountBalance {
  name: string
  balance: number
  type?: string
  subtype?: string
  scopeId?: number | string | null
  scope?: { id: number | string; name: string } | null
}

interface SpendingComparison {
  current_month_total: number
  average_monthly_spending: number
  difference: number
  percentage_difference: number
  projected_month_end: number
  remaining_budget: number
  current_day: number
  days_in_month: number
  active_months: number
  month_name: string
  is_above_average: boolean
  is_below_average: boolean
  has_remaining_budget: boolean
}

interface Category {
  CategoryName: string
  CategoryID?: number
}


// ============================================================================
// Sealed-row readiness
// ============================================================================
/**
 * True while any row this aggregation needs is still waiting to be decrypted.
 *
 * A sealed row arrives with `Amount = NULL` — the server can't read it — and
 * the value only appears once this client has libsodium up, the identity key
 * unlocked, the scope key unwrapped, and that row opened. `signedAmount()`
 * reads a missing Amount as 0, so every client-side aggregate computed inside
 * that window is silently short: totals come out as $0 and then jump to the
 * real figure a moment later.
 *
 * The plaintext columns give it away — `PlaidPending` and `ScopeID` are
 * readable immediately, which is why counts looked right while the money
 * didn't. Callers withhold their result while this is true rather than paint
 * a number that is about to change.
 *
 * Rows that were attempted and failed (`__decryptError`) do NOT hold anything
 * back: they will never resolve, and the rest of the app already renders them
 * as undecryptable rather than hiding the view.
 */
function rowsAwaitingDecrypt(
  list: any[] | undefined,
  only?: (row: any) => boolean,
): boolean {
  if (!list) return false
  for (const row of list) {
    if (only && !only(row)) continue
    if (row?.EncryptedPayload && !row?.__decrypted && !row?.__decryptError) {
      return true
    }
  }
  return false
}

// ============================================================================
// Query Factory
// ============================================================================

/**
 * Factory function for creating authenticated TanStack Query hooks.
 *
 * Features:
 * - Relies on TanStack Query's staleTime for caching (not enabled flag)
 * - Automatic authentication checks
 * - Consistent error handling
 * - Supports invalidation: invalidateQueries() triggers refetch when data is stale
 */
function createAuthenticatedQuery<TData = any, TError = Error, TSelectData = TData>(
  queryKey: readonly unknown[],
  // Reactive so a caller can vary the URL (the expenses query narrows it to a
  // trailing window until something needs deeper history). Resolved inside the
  // query function, so the key is what decides when to refetch — not the URL.
  endpoint: string | (() => string),
  auth: AuthContext,
  options?: Partial<UseQueryOptions<TData, TError, TSelectData>>
) {
  // Query is enabled whenever the user is authenticated.
  // Caching is handled by staleTime — no need to disable the query.
  const enabledCondition = computed(() => {
    return !!auth.isAuthenticated?.value
  })

  return useQuery<TData, TError, TSelectData>({
    queryKey,
    queryFn: async (): Promise<TData> => {
      // Wait out the SDK's session restore before deciding. Overview calls
      // refetch() on mount, which runs the query function regardless of
      // `enabled` — so without this a cold boot could throw here purely
      // because Auth0 hadn't finished starting, and the retries would all
      // fail the same way before the query gave up for good.
      await waitForAuthReady(auth)
      if (!auth.isAuthenticated?.value) {
        throw new Error('Not authenticated')
      }

      const url = typeof endpoint === 'function' ? endpoint() : endpoint
      const response = await authenticatedGet(url, auth)

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error ${response.status}: ${errorText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'API request failed')
      }

      return data
    },
    enabled: enabledCondition,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    ...options,
  })
}

// ============================================================================
// Query Hooks - Data Fetching
// ============================================================================

/**
 * Fetches user expenses with optional scope filtering.
 * Cached aggressively to minimize API calls.
 */
export function useExpensesQuery(auth: AuthContext) {
  // The key deliberately does NOT include scopeFilter. The request sends no
  // scope parameter — /api/get_expenses returns every accessible scope and the
  // filtering is done client-side by getFilteredExpenses — so keying on it
  // meant the whole ledger was re-fetched for a response that is byte-for-byte
  // identical. On a cold boot that fired twice: ScopeSwitcher flips
  // selectedScopes from [] to [ids] the moment /api/get_scopes lands, the key
  // changes underneath the in-flight request, and TanStack starts again.
  // 2.5 MB gzipped, downloaded twice, for one dataset. One key, one fetch,
  // shared by every caller regardless of the scope selection.
  // Keyed on depth so escalating to full history refetches, and on nothing
  // else — see the note above about scopeFilter.
  const { depth } = useExpenseHistory()
  const raw = createAuthenticatedQuery<ApiResponse<{ expenses: Expense[] }>, Error, Expense[]>(
    ['expenses', depth],
    () => expensesEndpoint.value,
    auth,
    {
      staleTime: CACHE_TIMES.EXPENSES,
      select: (data: ApiResponse<{ expenses: Expense[] }>) => data.expenses || [],
    }
  )

  // Transparent decryption: when the user is unlocked we pop each row's
  // sealed payload open and merge the inner fields back onto the row. The
  // computed depends on both the raw query data and the encryption state,
  // so unlocking after the data is fetched re-runs the merge with no
  // refetch needed.
  const enc = useEncryption()
  const decryptedData = computed<Expense[]>(() => {
    const rows = (raw.data.value || []) as Expense[]
    // Touch the unlock + scope-key state so unlocking (and the async
    // scope-key unwrap that follows) re-runs the merge with no refetch.
    const unlocked = !!(enc.state as any).privateKey
    void enc.state.scopeKeys.size
    if (!unlocked || !isSodiumReady()) return rows
    return rows.map((row) => decryptExpenseRow(row, enc))
  })

  // Swap the data ref out without disturbing isLoading/isFetching/etc.
  return Object.assign({}, raw, { data: decryptedData })
}

/** Open a sealed expense row under its SCOPE's key (Phase 4) and merge the
 *  inner fields back on. Tries the row's own scope key first, then every
 *  other key we hold: a row that MOVED scopes keeps the ciphertext of the
 *  scope it came from until the re-seal sweep repairs it (useReseal.ts), and
 *  it should stay visible meanwhile. The identity key is the last candidate,
 *  for legacy single-user rows whose scope has no key of its own. */
function decryptExpenseRow(row: any, enc: ReturnType<typeof useEncryption>): any {
  const sealed = row?.EncryptedPayload
  if (!sealed || typeof sealed !== 'string') return row
  const scopeId = typeof row?.ScopeID === 'number' ? row.ScopeID : Number(row?.ScopeID)
  // No keys at all means they haven't loaded yet — leave the row untouched
  // rather than flagging it as undecryptable.
  if (!enc.keysForScope(Number.isFinite(scopeId) ? scopeId : null)) return row
  const opened = enc.openSealedWithHeldKeys(sealed, Number.isFinite(scopeId) ? scopeId : null)
  if (!opened) return { ...row, __decryptError: true }
  return { ...row, ...opened, __decrypted: true }
}

// Pending Plaid expenses total across accessible scopes.
// Overview uses this to render "Including pending: $X" under net worth.
// Pending amounts use Plaid's sign convention (positive = outflow), so the
// caller computes `netWorth - pendingTotal` for the with-pending figure.
// Pass the global scope filter so the pending total matches the scoping of
// the net-worth figure it's compared against — otherwise an unscoped sum
// from Household accounts gets compared against Personal-scoped net worth.
export function usePendingTotalQuery(
  auth: AuthContext,
  scopeFilter?: Ref<number[] | null | undefined>,
) {
  // Treat empty array as "no filter" (matches isGlobalFilterActive semantics).
  const scopeIds = computed(() => {
    const v = scopeFilter?.value
    return Array.isArray(v) && v.length > 0 ? [...v].sort((a, b) => a - b) : null
  })
  const endpoint = computed(() =>
    scopeIds.value
      ? `/api/get_pending_total?scope_ids=${scopeIds.value.join(',')}`
      : '/api/get_pending_total'
  )
  const enc = useEncryption()
  // Shares one cache entry with every other caller — the endpoint is
  // scope-agnostic and the scope filtering below is client-side.
  const expensesQuery = useExpensesQuery(auth)

  const serverQuery = useQuery<
    ApiResponse<{ pending_total: number; pending_count: number }>,
    Error,
    { pending_total: number; pending_count: number }
  >({
    queryKey: ['pending-total', scopeIds],
    queryFn: async () => {
      await waitForAuthReady(auth)
      if (!auth.isAuthenticated?.value) throw new Error('Not authenticated')
      const response = await authenticatedGet(endpoint.value, auth)
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error ${response.status}: ${errorText}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'API request failed')
      return data
    },
    // Disable the server fetch for encrypted users — the server returns
    // 409 ENCRYPTED_AGGREGATION_UNAVAILABLE for them anyway and the
    // client branch below replaces it.
    enabled: computed(
      () => !!auth.isAuthenticated?.value && !enc.isEnabled.value,
    ),
    staleTime: CACHE_TIMES.BALANCES,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      pending_total: Number(data.pending_total || 0),
      pending_count: Number(data.pending_count || 0),
    }),
  })

  // A sealed row carries no plaintext Amount — the server sends NULL and the
  // value only appears once this client has libsodium up, the identity key
  // unlocked, the scope key unwrapped, and that row opened. signedAmount()
  // reads a missing Amount as 0, so a total computed mid-decrypt is silently
  // short: with every pending row still sealed it comes out as exactly $0,
  // and "With pending" renders equal to net worth before jumping to the real
  // figure. Withhold the total until no pending row is still waiting to be
  // opened. Rows that were attempted and failed (__decryptError) don't hold
  // it back — they'd never resolve, and the rest of the app already shows
  // those as undecryptable rather than hiding the view.
  const pendingAwaitingDecrypt = computed(
    () =>
      enc.isEnabled.value &&
      rowsAwaitingDecrypt(
        expensesQuery.data.value as any[] | undefined,
        (e) => !!e?.PlaidPending,
      ),
  )

  const data = computed(() => {
    if (enc.isEnabled.value) {
      const list = expensesQuery.data.value as any[] | undefined
      if (!list) return undefined
      if (pendingAwaitingDecrypt.value) return undefined
      return computePendingTotal(list, scopeIds.value, null)
    }
    return serverQuery.data.value
  })
  // Still "fetching" while rows are waiting on decryption: the number isn't
  // ready, and callers use this to keep a skeleton up rather than paint a
  // figure that is about to change.
  const isFetching = computed(() =>
    enc.isEnabled.value
      ? expensesQuery.isFetching.value || pendingAwaitingDecrypt.value
      : serverQuery.isFetching.value,
  )
  return Object.assign({}, serverQuery, { data, isFetching })
}

// Account balances query hook
export function useBalancesQuery(auth: AuthContext) {
  return createAuthenticatedQuery<any>(
    ['balances'],
    '/api/plaid_status',
    auth,
    {
      staleTime: CACHE_TIMES.BALANCES,
      select: (data) => data || {},
    }
  )
}

// Bank accounts query hook
export function useBankAccountsQuery(auth: AuthContext) {
  return createAuthenticatedQuery<ApiResponse<{ items: any[] }>, Error, any[]>(
    ['accounts'],
    '/api/get_plaid_items',
    auth,
    {
      staleTime: CACHE_TIMES.ACCOUNTS,
      select: (data) => data.items || [],
    }
  )
}

// Individual account details query hook
export function useAccountDetailsQuery(auth: AuthContext, itemId: string | null) {
  const enabledCondition = computed(() => {
    return !!auth.isAuthenticated?.value && !!itemId
  })

  const raw = useQuery({
    queryKey: ['account-details', itemId],
    queryFn: async () => {
      if (!itemId) return { accounts: [] }

      const response = await authenticatedGet(`/api/get_item_accounts?item_id=${itemId}`, auth)

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${response.status}: ${errorText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch account details')
      }

      return data
    },
    enabled: enabledCondition,
    staleTime: CACHE_TIMES.ACCOUNTS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
  })

  // Transparent decryption of per-account sealed payloads. The server
  // returns name/mask/type/balances as nulls + an `encrypted_payload`
  // base64 string for encrypted users; we decrypt and merge back so the
  // Overview's balance ribbon and the Accounts list render identically
  // regardless of whether the account is encrypted.
  const enc = useEncryption()
  const decryptedData = computed<any>(() => {
    const payload = raw.data.value
    if (!payload) return payload
    // Touch unlock + scope-key state so unlocking re-runs the merge.
    const unlocked = !!(enc.state as any).privateKey
    void enc.state.scopeKeys.size
    if (!unlocked || !isSodiumReady()) return payload
    // Use the canonical, scope-key-aware account decryptor (it keys off
    // each account's scope_id and coerces Decimal strings back to numbers).
    const accounts = Array.isArray(payload.accounts)
      ? payload.accounts.map((a: any) => enc.decryptPlaidAccount(a))
      : payload.accounts
    return { ...payload, accounts }
  })
  return Object.assign({}, raw, { data: decryptedData })
}

// Categories query hook
export function useCategoriesQuery(auth: AuthContext) {
  return createAuthenticatedQuery<ApiResponse<{ categories: Category[] }>>(
    ['categories'],
    '/api/get_categories',
    auth,
    {
      staleTime: CACHE_TIMES.CATEGORIES,
      select: (data: ApiResponse<{ categories: Category[] }>) => data.categories || [],
    }
  )
}

// Available categories query hook (includes user's expense categories)
export function useAvailableCategoriesQuery(auth: AuthContext) {
  return createAuthenticatedQuery<ApiResponse<{ categories: string[] }>>(
    ['available-categories'],
    '/api/get_available_categories',
    auth,
    {
      staleTime: CACHE_TIMES.CATEGORIES,
      select: (data: ApiResponse<{ categories: Category[] }>) => data.categories || [],
    }
  )
}

// Spending comparison query hook. Encrypted accounts compute the same
// projection locally from the decrypted expense list — the server can't
// sum NULL Amount columns. Non-encrypted accounts hit the SQL endpoint
// as before.
export function useSpendingComparisonQuery(auth: AuthContext) {
  const enc = useEncryption()
  const expensesQuery = useExpensesQuery(auth)
  const serverQuery = useQuery<
    ApiResponse<{ comparison: SpendingComparison }>,
    Error,
    SpendingComparison | null
  >({
    queryKey: ['spending-comparison'],
    queryFn: async () => {
      await waitForAuthReady(auth)
      if (!auth.isAuthenticated?.value) throw new Error('Not authenticated')
      const response = await authenticatedGet('/api/get_spending_comparison', auth)
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error ${response.status}: ${errorText}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'API request failed')
      return data
    },
    enabled: computed(
      () => !!auth.isAuthenticated?.value && !enc.isEnabled.value,
    ),
    staleTime: CACHE_TIMES.PROJECTIONS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    select: (data) => data.comparison ?? null,
  })

  const data = computed<SpendingComparison | null | undefined>(() => {
    if (enc.isEnabled.value) {
      const list = expensesQuery.data.value as any[] | undefined
      if (!list) return undefined
      // Every row feeds this total, so any sealed row still awaiting
      // decryption would silently count as $0.
      if (rowsAwaitingDecrypt(list)) return undefined
      return computeSpendingComparison(
        list,
        null,
        null,
      ) as ClientSpendingComparison | null
    }
    return serverQuery.data.value
  })
  const isFetching = computed(() =>
    enc.isEnabled.value
      ? expensesQuery.isFetching.value ||
        rowsAwaitingDecrypt(expensesQuery.data.value as any[] | undefined)
      : serverQuery.isFetching.value,
  )
  return Object.assign({}, serverQuery, { data, isFetching })
}

// Tracked categories query — replaces the previous three (targets / progress
// / averages) with a single response shaped per-scope. Pass the global scope
// filter so the result matches what the rest of the Overview shows.
//
// Shape:
//   { scopes: [{ scope_id, scope_name, scope_type,
//       categories: [{ category, trailing_12m_total, trailing_3m_avg,
//         current_month_spent, threshold, threshold_source: 'auto'|'override',
//         pct_of_threshold, is_over }] }],
//     top_n, month }
export interface TrackedCategoryRow {
  category: string
  trailing_12m_total: number
  trailing_3m_avg: number
  current_month_spent: number
  threshold: number
  threshold_source: 'auto' | 'override'
  pct_of_threshold: number
  is_over: boolean
}
export interface TrackedScopeBlock {
  scope_id: number
  scope_name: string
  scope_type: string
  categories: TrackedCategoryRow[]
}
export interface TrackedCategoriesResponse {
  scopes: TrackedScopeBlock[]
  top_n: number
  month: string
}

export function useTrackedCategoriesQuery(
  auth: AuthContext,
  scopeFilter?: Ref<number[] | null | undefined>,
  topN?: Ref<number>,
) {
  const scopeIds = computed(() => {
    const v = scopeFilter?.value
    return Array.isArray(v) && v.length > 0 ? [...v].sort((a, b) => a - b) : null
  })
  const top = computed(() => {
    const t = topN?.value ?? 5
    return Math.max(1, Math.min(10, Math.floor(t)))
  })
  const endpoint = computed(() => {
    const params = new URLSearchParams()
    if (scopeIds.value) params.set('scope_ids', scopeIds.value.join(','))
    params.set('top_n', String(top.value))
    return `/api/get_tracked_categories?${params.toString()}`
  })
  const enc = useEncryption()
  const expensesQuery = useExpensesQuery(auth)
  const { globalAvailableScopes } = useGlobalScopeFilter()
  // For encrypted accounts the client needs the raw category-target rows
  // (overrides + IsActive flags) to mirror the server's aggregation. The
  // endpoint is plaintext-safe — it only stores threshold metadata, not
  // ledger amounts.
  const targetsQuery = useQuery<
    ApiResponse<{ targets: CategoryTargetPref[] }>,
    Error,
    CategoryTargetPref[]
  >({
    queryKey: ['category-targets'],
    queryFn: async () => {
      await waitForAuthReady(auth)
      if (!auth.isAuthenticated?.value) throw new Error('Not authenticated')
      const response = await authenticatedGet('/api/get_category_targets', auth)
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error ${response.status}: ${errorText}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'API request failed')
      return data
    },
    enabled: computed(
      () => !!auth.isAuthenticated?.value && enc.isEnabled.value,
    ),
    staleTime: CACHE_TIMES.CATEGORY_TARGETS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    select: (data) => data.targets ?? [],
  })

  // Flag tracked locally: when the server returns 409
  // ENCRYPTED_AGGREGATION_UNAVAILABLE, we know the user IS encrypted on
  // the server even if our local state hasn't caught up. Flip this and
  // force the client-side branch below.
  const serverSaysEncrypted = ref(false)
  const localQueryClient = useQueryClient()

  const serverQuery = useQuery<
    ApiResponse<TrackedCategoriesResponse>,
    Error,
    TrackedCategoriesResponse
  >({
    queryKey: ['tracked-categories', scopeIds, top],
    queryFn: async () => {
      await waitForAuthReady(auth)
      if (!auth.isAuthenticated?.value) throw new Error('Not authenticated')
      const response = await authenticatedGet(endpoint.value, auth)
      if (response.status === 409) {
        const data = await response.json().catch(() => ({}))
        if (data?.code === 'ENCRYPTED_AGGREGATION_UNAVAILABLE') {
          serverSaysEncrypted.value = true
          // Wipe the persisted/cached previous response. Without this
          // step, TanStack's `placeholderData: previousData` keeps
          // painting whatever stale snapshot was in localStorage
          // (typically: $0 categories from an earlier broken session),
          // and the user sees garbage forever even after a hard reload.
          localQueryClient.removeQueries({ queryKey: ['tracked-categories'] })
          // Resync the encryption composable state.
          enc.load(auth).catch(() => { /* noop */ })
          throw new Error('ENCRYPTED_AGGREGATION_UNAVAILABLE')
        }
      }
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error ${response.status}: ${errorText}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'API request failed')
      // Successful response — clear the override.
      serverSaysEncrypted.value = false
      return data
    },
    enabled: computed(
      () => !!auth.isAuthenticated?.value && !enc.isEnabled.value && !serverSaysEncrypted.value,
    ),
    staleTime: CACHE_TIMES.CATEGORY_TARGETS,
    // Always refetch on mount: tracked categories is one of the widgets
    // most easily desynced (the server gate fires on a state change the
    // client doesn't know about) and the cost of an extra GET is small.
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      scopes: data.scopes || [],
      top_n: data.top_n || 5,
      month: data.month || '',
    }),
  })

  const data = computed<TrackedCategoriesResponse | undefined>(() => {
    if (enc.isEnabled.value || serverSaysEncrypted.value) {
      const list = expensesQuery.data.value as any[] | undefined
      if (!list) return undefined
      if (rowsAwaitingDecrypt(list)) return undefined
      const targets = targetsQuery.data.value ?? []
      const scopes = globalAvailableScopes.value as Array<{
        id: number; name: string; type: string
      }>
      const out = computeTrackedCategories(
        list,
        targets,
        scopes,
        scopeIds.value,
        top.value,
        null,
      ) as ClientTrackedCategoriesResponse
      return out
    }
    return serverQuery.data.value
  })
  const isFetching = computed(() =>
    enc.isEnabled.value
      ? expensesQuery.isFetching.value ||
        targetsQuery.isFetching.value ||
        rowsAwaitingDecrypt(expensesQuery.data.value as any[] | undefined)
      : serverQuery.isFetching.value,
  )
  return Object.assign({}, serverQuery, { data, isFetching })
}

// Plaid status query hook
export function usePlaidStatusQuery(auth: AuthContext) {
  return createAuthenticatedQuery<any>(
    ['plaid-status'],
    '/api/plaid_status',
    auth,
    {
      staleTime: CACHE_TIMES.PLAID_STATUS,
    }
  )
}

// Expense details query hook (for specific category/scope/month). For
// encrypted users the server filter on ExpenseCategory matches nothing
// (the column is NULL); the client filters the already-decrypted list.
export function useExpenseDetailsQuery(
  auth: AuthContext,
  params: { category?: string; scope_id?: number; month?: string } | null
) {
  const queryParams = new URLSearchParams()
  if (params?.category) queryParams.set('category', params.category)
  if (params?.scope_id) queryParams.set('scope_id', params.scope_id.toString())
  if (params?.month) queryParams.set('month', params.month)

  const enc = useEncryption()
  const expensesQuery = useExpensesQuery(auth)

  const serverQuery = createAuthenticatedQuery<
    ApiResponse<{ expenses: Expense[]; count: number }>,
    Error,
    { expenses: Expense[]; count: number }
  >(
    ['expense-details', params],
    `/api/get_expenses_by_category?${queryParams}`,
    auth,
    {
      enabled: computed(
        () => !!auth?.isAuthenticated?.value && !!params && !enc.isEnabled.value,
      ),
      staleTime: CACHE_TIMES.EXPENSES,
      select: (data) => ({
        expenses: data.expenses || [],
        count: data.count || 0,
      }),
    }
  )

  const data = computed<{ expenses: Expense[]; count: number } | undefined>(() => {
    if (!params) return undefined
    if (enc.isEnabled.value) {
      const list = expensesQuery.data.value as any[] | undefined
      if (!list) return undefined
      if (rowsAwaitingDecrypt(list)) return undefined
      const out = computeExpensesByCategory(list, {
        category: params.category,
        scope_id: params.scope_id,
        month: params.month ?? null,
      })
      return { expenses: out.expenses as unknown as Expense[], count: out.count }
    }
    return serverQuery.data.value
  })
  const isFetching = computed(() =>
    enc.isEnabled.value
      ? expensesQuery.isFetching.value ||
        rowsAwaitingDecrypt(expensesQuery.data.value as any[] | undefined)
      : serverQuery.isFetching.value,
  )
  return Object.assign({}, serverQuery, { data, isFetching })
}

// ============================================================================
// Mutation Hooks - Data Modifications
// ============================================================================
export function useCreateExpensesMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (expenses: any[]) => {
      console.log('Creating expenses:', expenses.length)
      const response = await authenticatedPost('/api/submit_expenses', { expenses }, auth)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${response.status}: ${errorText}`)
      }
      
      const data = await response.json()
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create expenses')
      }
      
      return data
    },
    onSuccess: (data, variables) => {
      console.log('Expenses created successfully, invalidating cache')
      invalidateFinancialData(queryClient)
      emitCacheInvalidationEvent(CACHE_EVENTS.EXPENSE_CREATED, { count: variables.length })
    },
    onError: (error) => {
      console.error('Failed to create expenses:', error)
    },
  })
}

/**
 * Walk every cached `['expenses', ...]` query and find the raw row by
 * ExpenseID. Raw means with `EncryptedPayload` still as base64 (not yet
 * decrypted) — the encrypted-update path needs the original sealed
 * payload + the plaintext non-sensitive fields to merge edits onto.
 */
function findRawCachedExpense(
  queryClient: ReturnType<typeof useQueryClient>,
  expenseId: number,
): any | null {
  const entries = queryClient.getQueriesData({ queryKey: ['expenses'] })
  for (const [, data] of entries) {
    const list = (data as any)?.expenses ?? data
    if (!Array.isArray(list)) continue
    for (const row of list) {
      if (row?.ExpenseID === expenseId) return row
    }
  }
  return null
}

const EXPENSE_ENCRYPTED_FIELDS = [
  'Amount',
  'AdjustedAmount',
  'ExpenseCategory',
  'AdditionalNotes',
  'Tags',
  'MerchantName',
  'PlaidAmount',
  'PlaidMerchantName',
  'PlaidName',
  'IsIncome',
  'PlaidPersonalFinanceCategoryPrimary',
  'PlaidPersonalFinanceCategoryDetailed',
  // Location — must stay in lockstep with shared/encryption.py. A sealed
  // field missing from this list is silently dropped on the next re-seal,
  // so an edit to one expense would erase where it happened.
  'LocationLat',
  'LocationLon',
  'LocationAddress',
  'LocationCity',
  'LocationRegion',
  'LocationPostalCode',
  'LocationCountry',
] as const

/**
 * Build the sealed payload for an encrypted-update.
 *
 * The user's edits arrive as the same `{category, amount, merchant, tags,
 * ...}` shape the legacy path expects. We need to translate those onto the
 * server's payload schema (ExpenseCategory/Amount/MerchantName/...) and
 * merge over the row's current decrypted values so we don't lose fields
 * that weren't edited (e.g. PlaidMerchantName when only category changed).
 *
 * Returns a base64 string ready to drop in the body as EncryptedPayload.
 */
async function buildSealedPayload(
  enc: ReturnType<typeof useEncryption>,
  rawRow: any,
  edits: any,
  destScopeId?: number | null,
): Promise<string> {
  // Start from the row's currently-decrypted values. decryptRow keys off
  // rawRow.ScopeID, so this opens the payload under the row's CURRENT scope.
  const decrypted = await enc.decryptRow(rawRow)
  // ABORT if the row was sealed but could not be decrypted: re-sealing here
  // would build an all-NULL payload, and the server nulls every plaintext
  // mirror column to match, irreversibly destroying the row's data. This
  // happens when the scope key isn't unlocked yet (post-unlock unwrap race, or
  // a shared-scope key that failed to load) — fail loudly so the user can
  // unlock and retry instead of silently losing their data.
  if ((decrypted as any).__decryptError) {
    throw new Error(
      'This expense could not be decrypted (encryption key not unlocked). ' +
        'Unlock encryption and try again.',
    )
  }
  const payload: Record<string, unknown> = {}
  for (const k of EXPENSE_ENCRYPTED_FIELDS) {
    payload[k] = (decrypted as any)[k] ?? null
  }

  // Apply the editor's edits onto the payload schema
  if (edits.category != null) payload.ExpenseCategory = edits.category
  if (edits.merchant != null) payload.MerchantName = edits.merchant
  // Upstream merge: values Plaid has that the server couldn't write, because
  // it can't decrypt a sealed row to merge them (see the sealed-merge
  // endpoints in plaid_routes.py). Restricted to sealed fields so a stray key
  // can't smuggle anything else into the payload; the server already limits
  // the set to fields the user doesn't own.
  if (edits.upstream) {
    for (const [k, v] of Object.entries(edits.upstream)) {
      if ((EXPENSE_ENCRYPTED_FIELDS as readonly string[]).includes(k)) payload[k] = v
    }
  }
  // A location the user picked. The coordinates are sealed like everything
  // else; the server only learns who chose it (LocationSource='manual'), which
  // is what stops the next sync overwriting it.
  if (edits.location !== undefined) {
    const loc = (edits.location || {}) as Record<string, unknown>
    payload.LocationLat = loc.lat ?? null
    payload.LocationLon = loc.lon ?? null
    payload.LocationCity = loc.city ?? null
    payload.LocationRegion = loc.region ?? null
    payload.LocationCountry = loc.country ?? null
    payload.LocationAddress = loc.address ?? null
    payload.LocationPostalCode = loc.postal_code ?? null
  }
  if (edits.tags !== undefined) {
    payload.Tags = serializeTags(edits.tags)
    // The editor seeds its tag list from expenseTags(), which already folded
    // any pre-tags note in — so the note is now redundant. Clearing it here
    // is what migrates a sealed legacy note, since the server can't.
    payload.AdditionalNotes = null
  }
  if (edits.amount != null) {
    payload.Amount = typeof edits.amount === 'string'
      ? Number(edits.amount.replace(/,/g, ''))
      : Number(edits.amount)
  }
  // IsIncome derives from category for manual entries; preserve the
  // existing value otherwise so refunds (negative amounts) stay flagged
  // the way the server originally categorised them.
  if (typeof edits.category === 'string') {
    payload.IsIncome = edits.category.startsWith('Income') || edits.category === 'Income'
  }

  // Re-seal under the DESTINATION scope's key (where the row will live after
  // this edit). For a plain edit that's the row's own scope; for a scope
  // *move* it's the target scope — so co-members of the destination can read
  // it. Falls back to the identity key inside keysForScope for legacy rows.
  const sealScope =
    destScopeId != null && Number.isFinite(Number(destScopeId))
      ? Number(destScopeId)
      : (Number.isFinite(Number(rawRow?.ScopeID)) ? Number(rawRow.ScopeID) : undefined)
  return enc.sealExpensePayload(payload, sealScope)
}

export function useUpdateExpenseMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  const enc = useEncryption()

  return useMutation({
    mutationFn: async (expense: any) => {
      console.log('Updating expense:', expense.ExpenseID)

      // Encrypted users: re-seal the row locally before posting. The
      // server has the public key but no way to decrypt-then-mutate.
      let body: any = { expenses: [expense] }
      if (enc.isEnabled.value) {
        const raw = findRawCachedExpense(queryClient, expense.ExpenseID)
        if (!raw) {
          throw new Error(
            'Cannot update: this expense isn\'t in cache. Refresh the page and try again.',
          )
        }
        // Encrypted writes carry ScopeID directly (the legacy path looks up
        // scope by name). This is also the destination scope to seal under.
        const destScope = expense.ScopeID ?? expense.scopeId ?? expense.scope
        const sealedB64 = await buildSealedPayload(enc, raw, expense, destScope)
        body = {
          expenses: [{
            ...expense,
            EncryptedPayload: sealedB64,
            ScopeID: destScope,
          }],
        }
      }

      const response = await authenticatedPut('/api/update_expense', body, auth)

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${response.status}: ${errorText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to update expense')
      }

      return data
    },
    onSuccess: (data, variables) => {
      console.log('Expense updated successfully, invalidating cache')
      invalidateFinancialData(queryClient)
      emitCacheInvalidationEvent(CACHE_EVENTS.EXPENSE_UPDATED, { expenseId: variables.ExpenseID })
    },
    onError: (error) => {
      console.error('Failed to update expense:', error)
    },
  })
}

export function useBulkUpdateExpensesMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  const enc = useEncryption()

  return useMutation({
    mutationFn: async ({ expenseIds, updates }: { expenseIds: number[]; updates: any }) => {
      console.log('Bulk updating expenses:', expenseIds.length)

      let body: any = { expenseIds, updates }

      // Tag edits are per-row: `addTags`/`removeTags` merge into whatever
      // each row already carries, so the final set differs row by row and
      // the server can't compute it (the tags are sealed for encrypted
      // scopes, and for plaintext ones this saves a read-modify-write). We
      // resolve it here from the cache, where every row is decrypted.
      // `updates.tags` (replace every selected row with one set) needs none
      // of that and goes through as a plain update.
      const mergesTags = updates.addTags != null || updates.removeTags != null
      const resolvedTags: Record<number, string[]> = {}
      if (mergesTags) {
        for (const eid of expenseIds) {
          const raw = findRawCachedExpense(queryClient, eid)
          if (!raw) {
            throw new Error(
              `Cannot update ${eid}: expense not in cache. Refresh the page and try again.`,
            )
          }
          // Encrypted rows keep their tags in the sealed payload, so open the
          // row before reading them. decryptRow is a no-op for plaintext rows.
          const current = expenseTags(await enc.decryptRow(raw))
          resolvedTags[eid] = removeTags(
            addTags(current, updates.addTags),
            updates.removeTags,
          )
        }
        // Per-row map for the plaintext path; the encrypted path folds the
        // same values into each sealed payload below.
        body.expenseTags = Object.fromEntries(
          Object.entries(resolvedTags).map(([id, tags]) => [id, serializeTags(tags)]),
        )
        // These are client-side directives, not columns — don't ship them.
        const { addTags: _a, removeTags: _r, ...rest } = updates
        body.updates = rest
      }

      // Encrypted users: produce one re-sealed payload per row by
      // looking up the cached row, decrypting it, applying the bulk
      // update fields, and re-sealing. The server then writes each row
      // individually. Sensitive bulk updates (category/tags) only land
      // through the sealed payloads — they're stripped from the
      // plaintext update_values server-side.
      if (enc.isEnabled.value) {
        const sealedMap: Record<number, string> = {}
        // A bulk scope-move (updates.scope) re-homes every selected row to
        // one destination scope, so seal under that scope's key; otherwise
        // each row stays in its own scope (buildSealedPayload falls back to
        // rawRow.ScopeID when destScope is undefined).
        const destScope =
          updates.scope != null && Number.isFinite(Number(updates.scope))
            ? Number(updates.scope)
            : undefined
        for (const eid of expenseIds) {
          const raw = findRawCachedExpense(queryClient, eid)
          if (!raw) {
            throw new Error(
              `Cannot update ${eid}: expense not in cache. Refresh the page and try again.`,
            )
          }
          const editsForSeal: any = {
            // Bulk updates use these keys (see ViewExpenses); translate
            // them onto what buildSealedPayload expects.
            category: updates.category,
            tags: mergesTags ? resolvedTags[eid] : updates.tags,
            // Bulk "set location". The server can only write the plaintext
            // LocationSource marker on a sealed row — the place itself has to
            // come through here, or every selected row ends up flagged "set
            // by you" with nothing inside it. Absent means unchanged
            // (buildSealedPayload keys off `!== undefined`).
            location: updates.location,
          }
          sealedMap[eid] = await buildSealedPayload(enc, raw, editsForSeal, destScope)
        }
        body.encryptedPayloads = sealedMap
      }

      const response = await authenticatedPost(
        '/api/bulk_update_expenses',
        body,
        auth,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${response.status}: ${errorText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to bulk update expenses')
      }

      return data
    },
    onSuccess: (data, variables) => {
      console.log('Expenses bulk updated successfully, invalidating cache')
      invalidateFinancialData(queryClient)
      emitCacheInvalidationEvent(CACHE_EVENTS.EXPENSE_BULK_UPDATED, { count: variables.expenseIds.length })
    },
    onError: (error) => {
      console.error('Failed to bulk update expenses:', error)
    },
  })
}

/**
 * One-time backfill of transaction locations onto existing rows.
 *
 * Needed because the sync path can't retrofit them: a full re-pull returns
 * every transaction as "added" and the insert dedupes on PlaidTransactionID,
 * so rows that predate the map never gain a location.
 *
 * Runs per Plaid item so a big ledger doesn't ride on one long request:
 *   1. POST — server pulls the item's locations from Plaid, writes the ones on
 *      unsealed rows itself, and returns the sealed rows it can't touch.
 *   2. Here — decrypt each of those rows, merge the location in, re-seal.
 *   3. PUT — store the re-sealed payloads.
 * Step 2 is the whole reason this isn't just a script: the server has no way
 * to open a sealed row, so only this browser can put a location inside one.
 */
export function useBackfillLocationsMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  const enc = useEncryption()

  return useMutation({
    mutationFn: async (
      opts: {
        itemIds?: (string | null)[]
        onProgress?: (done: number, total: number) => void
        /** Only rows the sync flagged as waiting on this client. Cheap enough
         *  to run unattended: the server checks the flag before calling Plaid
         *  and returns immediately when nothing is waiting. */
        pendingOnly?: boolean
      } = {},
    ) => {
      // No item list? Let the server sweep everything in one call.
      const itemIds = opts.itemIds?.length ? opts.itemIds : [null]
      let updated = 0
      let resealed = 0
      let located = 0
      const failedItems: string[] = []

      for (const [i, itemId] of itemIds.entries()) {
        opts.onProgress?.(i, itemIds.length)

        const response = await authenticatedPost(
          '/api/plaid/backfill_locations',
          {
            ...(itemId ? { item_id: itemId } : {}),
            ...(opts.pendingOnly ? { pending_only: true } : {}),
          },
          auth,
        )
        if (!response.ok) {
          throw new Error(`${response.status}: ${await response.text()}`)
        }
        const data = await response.json()
        if (!data.success) throw new Error(data.error || 'Location backfill failed')

        updated += Number(data.updated || 0)
        located += Number(data.located || 0)
        if (Array.isArray(data.failedItems)) failedItems.push(...data.failedItems)

        const sealed: Record<string, Record<string, unknown>> = data.sealed || {}
        const sealedIds = Object.keys(sealed)
        if (!sealedIds.length) continue

        // Re-seal locally. A row we can't decrypt is skipped rather than
        // written — buildSealedPayload throws there, and sealing a row we
        // couldn't read would replace it with an empty payload.
        // Phase 1 can't tell which sealed rows already hold a location — their
        // plaintext mirror is NULL by definition, so it hands back every one.
        // Only this side can see inside, so the "already done" check lives
        // here. It matters because re-running is the way to pick up locations
        // Plaid improves when a pending transaction settles: without this,
        // every run would re-seal the entire ledger.
        const payloads: Record<string, string> = {}
        for (const id of sealedIds) {
          const raw = findRawCachedExpense(queryClient, Number(id))
          if (!raw) continue
          try {
            const current = await enc.decryptRow(raw)
            const incoming = sealed[id] as Record<string, unknown>
            const unchanged = Object.entries(incoming).every(
              ([k, v]) =>
                (current as any)[k] === v ||
                ((current as any)[k] == null && v == null) ||
                // Amounts round-trip through JSON as number|string.
                (v != null && Number((current as any)[k]) === Number(v)),
            )
            if (unchanged) continue
            payloads[id] = await buildSealedPayload(enc, raw, { upstream: incoming })
          } catch (e) {
            console.warn(`[backfill] skipped expense ${id}:`, e)
          }
        }

        if (Object.keys(payloads).length) {
          const put = await authenticatedPut(
            '/api/plaid/backfill_locations',
            { encryptedPayloads: payloads },
            auth,
          )
          if (!put.ok) throw new Error(`${put.status}: ${await put.text()}`)
          const putData = await put.json()
          if (!putData.success) throw new Error(putData.error || 'Failed to save locations')
          resealed += Number(putData.updated || 0)
        }
      }

      opts.onProgress?.(itemIds.length, itemIds.length)
      return { updated, resealed, located, failedItems }
    },
    onSuccess: () => {
      invalidateFinancialData(queryClient)
    },
    onError: (error) => {
      console.error('Failed to backfill locations:', error)
    },
  })
}

export function useDeleteExpensesMutation(auth: AuthContext) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (expenseIds: number[]) => {
      console.log('Deleting expenses:', expenseIds)
      const response = await authenticatedPost('/api/delete_expenses', { expenseIds }, auth)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${response.status}: ${errorText}`)
      }
      
      const data = await response.json()
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete expenses')
      }
      
      return data
    },
    onSuccess: (data, variables) => {
      console.log('Expenses deleted successfully, invalidating cache')
      invalidateFinancialData(queryClient)
      emitCacheInvalidationEvent(CACHE_EVENTS.EXPENSE_DELETED, { count: variables.length })
    },
    onError: (error) => {
      console.error('Failed to delete expenses:', error)
    },
  })
}

// Hook to prefetch likely-to-be-needed data
export function usePrefetchQueries(auth: AuthContext) {
  const queryClient = useQueryClient()
  
  const prefetchExpenses = () => {
    queryClient.prefetchQuery({
      queryKey: ['expenses'],
      queryFn: async () => {
        const response = await authenticatedGet('/api/get_expenses', auth)
        return response.json()
      },
      staleTime: CACHE_TIMES.EXPENSES,
    })
  }
  
  const prefetchBalances = () => {
    queryClient.prefetchQuery({
      queryKey: ['balances'],
      queryFn: async () => {
        const response = await authenticatedGet('/api/plaid_status', auth)
        return response.json()
      },
      staleTime: CACHE_TIMES.BALANCES,
    })
  }
  
  return {
    prefetchExpenses,
    prefetchBalances,
  }
}
